/**
 * ChatActionsSheet
 *
 * Helper to open the chat-actions bottom sheet via SheetService. Opened from
 * `ChatPage` when the user taps "More" in the chat app bar. Mirrors the
 * `SubscribeUserSheet.tsx` opener pattern.
 *
 * Usage:
 *   ChatActionsSheet.show({
 *     peerId: chatUsersInfo.peerId,
 *     peerFirstName,
 *     screenKey,
 *   });
 *
 * Flutter: lib/domain/entity/bottom_sheet/chat_actions_bottom_sheet.dart
 *   — the three-row actions sheet (block conversation / block user / mute).
 *   The drag-to-dismiss / `AnimatedPositioned` chrome is Flutter-only; the web
 *   sheet shell is provided by `SheetService`.
 */
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import { ChatActionsSheetContent } from './ChatActionsSheetContent';

export interface ChatActionsSheetConfig {
  /** Peer user's firebase id — keys the three predicate reads. */
  peerId: string;
  /** Peer first name — interpolated into the block-user row label. */
  peerFirstName: string;
  /**
   * Invoked on a successful block/unblock-conversation mutation so the hosting
   * chat page can flip its `useChat.isConversationBlocked` state live (composer
   * "Conversation blocked" replacement + app-bar video-call-icon drop). Wired
   * by `ChatPage.handleMore` to `chat.setConversationBlocked`. Not called on
   * the failure path (the optimistic write was rolled back) — mirrors Flutter
   * only reflecting the confirmed-and-successful action.
   */
  onConversationBlockChanged?: (block: boolean) => void;
  /** Screen key of the hosting page — threaded into the sheet + InfoSheet. */
  screenKey?: string;
  /** Optional container override (HTMLElement, screen-key string, or null). */
  container?: HTMLElement | string | null;
}

const show = (config: ChatActionsSheetConfig): void => {
  SheetService.show({
    content: (
      <ChatActionsSheetContent
        peerId={config.peerId}
        peerFirstName={config.peerFirstName}
        onConversationBlockChanged={config.onConversationBlockChanged}
        screenKey={config.screenKey}
      />
    ),
    container: config.container,
    screenKey: config.screenKey,
    closeOnClickOutside: true,
    qaId: 'chat-actions',
  });
};

export const ChatActionsSheet = {
  show,
};
